import React, { ChangeEvent, FC, Fragment, useContext, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { addPdfTemplate, getPdfTemplate } from 'apollo/templates/pdf-template';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import Loader from 'components/shared/loader/loader.component';
import { LoadingButton } from '@mui/lab';
import { CONSTANTS } from 'config/constants';

const { TEMPLATE_NAME } = CONSTANTS;

type PdfTemplateDialogProps = {
    open: boolean;
    onCloseDialog: () => void;
    labId: string;
    onSave: () => void;
};
const PdfTemplateDialog: FC<PdfTemplateDialogProps> = ({
    open,
    onCloseDialog,
    labId,
    onSave,
}: PdfTemplateDialogProps) => {
    const { t: translate } = useTranslation(['common', 'organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [templateName, setTemplateName] = useState('');
    const [htmlContent, setHtmlContent] = useState('');
    const [enabled, setEnabled] = useState(false);

    // TEMPLATE_NAME is kept in key order; the visible labels mirror the document names used
    // elsewhere in the app, so sort on those to keep the dropdown alphabetical.
    const templateOptions = useMemo(
        () =>
            TEMPLATE_NAME.map((template) => ({
                value: template.value,
                label: translate(`organization:PDF_TEMPLATES.${template.key}`),
            })).sort((a, b) => a.label.localeCompare(b.label)),
        [translate],
    );

    const [savePdfTemplate, { loading: saveLoading }] = useMutation(addPdfTemplate, {
        onError: (err: Error) => {
            showBoundary(err);
        },
        onCompleted: () => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('common:CHANGES_SAVED') });
            onSave();
        },
    });

    const [pdfTemplate, { loading }] = useLazyQuery(getPdfTemplate, {
        fetchPolicy: 'network-only',
        onError: (err: Error) => {
            showBoundary(err);
        },
        onCompleted: ({ pdfTemplate }) => {
            if (!pdfTemplate) {
                setHtmlContent('');
                setEnabled(false);
                return;
            }
            setTemplateName(pdfTemplate.templateName);
            setHtmlContent(pdfTemplate.htmlContent);
            setEnabled(pdfTemplate.enabled);
        },
    });

    const handleSelectTypeOfImportChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setTemplateName(value);
        pdfTemplate({ variables: { labId: labId, templateName: value } });
    };

    const handleHtmlContentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setHtmlContent(value);
    };

    const handleTemplateEnabledCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        setEnabled(event.target.checked);
    };

    const handleClose = () => {
        setTemplateName('');
        setHtmlContent('');
        setEnabled(false);
        onCloseDialog();
    };

    const saveTemplate = async () => {
        await savePdfTemplate({
            variables: {
                templateName,
                htmlContent,
                enabled,
                lab: labId,
            },
        });
    };

    return (
        <Fragment>
            <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
                <DialogTitle>{translate('organization:PDF_TEMPLATE')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel>{translate('organization:TEMPLATE_NAME')}</InputLabel>
                                <Select
                                    value={templateName}
                                    label={translate('organization:TEMPLATE_NAME')}
                                    onChange={handleSelectTypeOfImportChange}
                                >
                                    {templateOptions.map((template) => (
                                        <MenuItem key={template.value} value={template.value}>
                                            {template.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {loading ? (
                            <Loader />
                        ) : (
                            <Fragment>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center">
                                        <Checkbox checked={enabled} onChange={handleTemplateEnabledCheckbox} />
                                        <Box>{translate('organization:ENABLE_TEMPLATE')}</Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        id="html-content"
                                        value={htmlContent}
                                        fullWidth
                                        disabled={!enabled || !templateName}
                                        label={translate('organization:HTML_CONTENT')}
                                        required
                                        multiline
                                        rows={15}
                                        margin="dense"
                                        onChange={handleHtmlContentChange}
                                    />
                                </Grid>
                            </Fragment>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{translate('common:CANCEL')}</Button>
                    <LoadingButton
                        disabled={!templateName || !htmlContent}
                        loading={saveLoading}
                        variant="contained"
                        type="submit"
                        onClick={saveTemplate}
                    >
                        {translate('common:SAVE')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default PdfTemplateDialog;
